import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMembershipsOpen, setIsMembershipsOpen] = useState(false);
  const [logoHover, setLogoHover] = useState(false);
  const reviewsRef = useRef(null);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const membershipsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reviewsRef.current && !reviewsRef.current.contains(event.target)) {
        setIsReviewsOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(event.target)) {
        setIsAboutOpen(false);
      }
      if (membershipsRef.current && !membershipsRef.current.contains(event.target)) {
        setIsMembershipsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId, isPage = false) => {
    if (isPage) {
      navigate(`/${sectionId}`);
      setIsMobileMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // If we're on a different page, navigate home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 90;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', id: 'hero' },
    { 
      name: 'About Us', 
      isDropdown: true,
      items: [
        { name: 'About Us', id: 'about', isPage: true },
        { name: 'FAQs', id: 'faq', isPage: true },
        { name: 'Privacy Policy', id: 'privacy-policy', isPage: true }
      ]
    },
    { 
      name: 'Services', 
      isDropdown: true,
      items: [
        { name: 'Injectables', id: 'injectables', isPage: true },
        { name: 'Chemical Peels', id: 'chemical-peels', isPage: true },
        { name: 'Facials', id: 'facials', isPage: true },
        { name: 'Body Treatments', id: 'body-treatments', isPage: true },
        { name: 'IV Therapy', id: 'iv-therapy', isPage: true },
        { name: 'Mens Health', id: 'mens-health', isPage: true },
        { name: 'Peptides', id: 'peptides', isPage: true },
        { name: 'Muscle Sculpting', id: 'muscle-sculpting', isPage: true },
        { name: 'Massage', id: 'massage', isPage: true },
        { name: 'Laser Hair Removal', id: 'laser-hair-removal', isPage: true },
        { name: 'Waxing', id: 'waxing', isPage: true },
        { name: 'Medical Weight Loss', id: 'medical-weight', isPage: true },
        { name: 'Pigmented Lesion', id: 'pigmented-lesion', isPage: true },
        { name: 'Vascular Lesion', id: 'vascular-lesion', isPage: true },
        { name: 'Skin Tags', id: 'skin-tag', isPage: true }
      ]
    },
    { 
      name: 'Memberships', 
      isDropdown: true,
      items: [
        { name: 'Exclusive Memberships', id: 'exclusive-memberships', isPage: true },
        { name: 'Men\'s Memberships', id: 'mens-memberships', isPage: true }
      ]
    },
    { 
      name: 'Reviews', 
      isDropdown: true,
      items: [
        { name: 'Testimonials', id: 'testimonials' },
        { name: 'Google Reviews', id: 'google-reviews', isPage: true }
      ]
    },
    { name: 'Contact', id: 'contact' },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg' 
            : 'bg-white'
        }`}
      >
        <nav className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3">
          <div className="flex items-center gap-3 sm:gap-4 lg:gap-5 xl:gap-7">
            {/* Logo - Responsive sizing */}
            <div 
              className="flex items-center cursor-pointer flex-shrink-0 transition-all duration-300" 
              onClick={() => scrollToSection('hero')}
              onMouseEnter={() => setLogoHover(true)}
              onMouseLeave={() => setLogoHover(false)}
            >
              <img 
                src="/longevity.png" 
                alt="Century Longevity & Aesthetics" 
                className="h-12 sm:h-16 lg:h-20 xl:h-24 w-auto transition-all duration-300"
                style={{
                  filter: logoHover 
                    ? 'brightness(0) saturate(100%) invert(58%) sepia(42%) saturate(426%) hue-rotate(5deg) brightness(92%) contrast(86%)'
                    : 'none'
                }}
              />
            </div>

            {/* Desktop Navigation Links */}
            <ul className="hidden lg:flex items-center gap-5 xl:gap-7 flex-1">
              {navLinks.map((link) => (
                <li 
                  key={link.name} 
                  className="relative" 
                  ref={
                    link.name === 'Reviews' ? reviewsRef : 
                    link.name === 'Services' ? servicesRef : 
                    link.name === 'About Us' ? aboutRef :
                    link.name === 'Memberships' ? membershipsRef : null
                  }
                >
                  {link.isDropdown ? (
                    <div>
                      <button
                        onClick={() => {
                          if (link.name === 'Reviews') setIsReviewsOpen(!isReviewsOpen);
                          if (link.name === 'Services') setIsServicesOpen(!isServicesOpen);
                          if (link.name === 'About Us') setIsAboutOpen(!isAboutOpen);
                          if (link.name === 'Memberships') setIsMembershipsOpen(!isMembershipsOpen);
                        }}
                        className="text-olive-darkest hover:text-gold transition-colors duration-300 font-medium text-[0.85rem] xl:text-[0.9rem] tracking-wide flex items-center gap-1 group"
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                          (link.name === 'Reviews' && isReviewsOpen) || 
                          (link.name === 'Services' && isServicesOpen) ||
                          (link.name === 'About Us' && isAboutOpen) ||
                          (link.name === 'Memberships' && isMembershipsOpen)
                            ? 'rotate-180' 
                            : ''
                        }`} />
                      </button>
                      <AnimatePresence>
                        {((link.name === 'Reviews' && isReviewsOpen) || 
                          (link.name === 'Services' && isServicesOpen) ||
                          (link.name === 'About Us' && isAboutOpen) ||
                          (link.name === 'Memberships' && isMembershipsOpen)) && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-olive-medium/10 py-2 min-w-[200px] z-50"
                          >
                            {link.items.map((item) => (
                              <button
                                key={item.name}
                                onClick={() => {
                                  scrollToSection(item.id, item.isPage);
                                  setIsReviewsOpen(false);
                                  setIsServicesOpen(false);
                                  setIsAboutOpen(false);
                                  setIsMembershipsOpen(false);
                                }}
                                className="block w-full text-left px-4 py-2 text-olive-darkest hover:bg-cream hover:text-gold transition-colors duration-300 text-[0.85rem] xl:text-[0.9rem]"
                              >
                                {item.name}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.id, link.isPage)}
                      className="text-olive-darkest hover:text-gold transition-colors duration-300 font-medium text-[0.85rem] xl:text-[0.9rem] tracking-wide"
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              {/* Care Credit Link */}
              <a
                href="https://www.carecredit.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-olive-darkest hover:text-gold transition-colors duration-300 font-medium text-[0.85rem] xl:text-[0.9rem] tracking-wide"
              >
                Care Credit
              </a>

              {/* Social Media Icons */}
              <div className="flex items-center gap-3 xl:gap-4">
                <a
                  href="https://www.instagram.com/century.aesthetics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-olive-darkest hover:text-gold transition-colors duration-300"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5 xl:w-6 xl:h-6" />
                </a>
              </div>

              {/* CTA Button - Boulevard Self-Booking */}
              <motion.a
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                href="#book-now"
                className="bg-olive-darkest text-white px-5 xl:px-7 py-2.5 xl:py-3 rounded font-medium text-[0.85rem] xl:text-[0.9rem] tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap inline-block"
              >
                Book Appointment
              </motion.a>
            </div>

            {/* Mobile Menu Button - Larger touch target */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-olive-darkest hover:text-gold transition-colors duration-300 p-2 ml-auto -mr-2 touch-manipulation"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-8 h-8 sm:w-7 sm:h-7" />
              ) : (
                <Menu className="w-8 h-8 sm:w-7 sm:h-7" />
              )}
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu - Improved spacing and touch targets */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed top-0 right-0 h-full w-[85%] sm:w-[80%] max-w-sm bg-white z-50 shadow-2xl lg:hidden overflow-y-auto"
          >
            <div className="p-6 sm:p-8">
              {/* Close Button - Larger touch target */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 text-olive-darkest hover:text-gold transition-colors duration-300 p-2 -m-2 touch-manipulation"
                aria-label="Close menu"
              >
                <X className="w-8 h-8 sm:w-7 sm:h-7" />
              </button>

              {/* Mobile Logo - Responsive sizing */}
              <div className="flex items-center mb-10 sm:mb-12 mt-2 sm:mt-4 pr-12">
                <img 
                  src="/longevity.png" 
                  alt="Century Longevity & Aesthetics" 
                  className="h-14 sm:h-16 w-auto"
                />
              </div>

              {/* Mobile Navigation Links - Better spacing and touch targets */}
              <nav className="space-y-5 sm:space-y-6 mb-10 sm:mb-12">
                {navLinks.map((link, index) => (
                  <div key={link.name}>
                    {link.isDropdown ? (
                      <div>
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="text-olive-darkest text-lg sm:text-xl font-medium mb-3 py-1"
                        >
                          {link.name}
                        </motion.div>
                        <div className="pl-4 space-y-3">
                          {link.items.map((item, subIndex) => (
                            <motion.button
                              key={item.name}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: (index + subIndex * 0.05) * 0.1 }}
                              onClick={() => scrollToSection(item.id, item.isPage)}
                              className="block w-full text-left text-olive-darkest text-base sm:text-lg hover:text-gold transition-colors duration-300 py-2 touch-manipulation"
                            >
                              {item.name}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <motion.button
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => scrollToSection(link.id, link.isPage)}
                        className="block w-full text-left text-olive-darkest text-lg sm:text-xl font-medium hover:text-gold transition-colors duration-300 py-1 touch-manipulation"
                      >
                        {link.name}
                      </motion.button>
                    )}
                  </div>
                ))}
              </nav>

              {/* Care Credit Link - Mobile */}
              <motion.a
                href="https://www.carecredit.com/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="block w-full text-center text-olive-darkest text-lg sm:text-xl font-medium hover:text-gold transition-colors duration-300 py-3 mb-6 border border-gold/30 rounded touch-manipulation"
              >
                Care Credit
              </motion.a>

              {/* Mobile CTA Button - Boulevard Self-Booking */}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                href="#book-now"
                className="block w-full bg-olive-darkest text-white px-6 py-4 sm:py-4 rounded font-medium text-base sm:text-base tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg mb-6 sm:mb-8 touch-manipulation text-center"
              >
                Book Appointment
              </motion.a>

              {/* Mobile Social Media Icons - Larger touch targets */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-6 sm:gap-6 pt-6 sm:pt-8 border-t border-gold/20"
              >
                <a
                  href="https://www.instagram.com/century.aesthetics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-olive-darkest hover:text-gold transition-colors duration-300 p-2 -m-2 touch-manipulation"
                  aria-label="Instagram"
                >
                  <Instagram className="w-9 h-9 sm:w-8 sm:h-8" />
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;